import React from 'react';
import { getBreakpointFromWidth } from 'react-grid-layout/build/responsiveUtils';
import { getContext, isAnimationDisabled, useCombinedRefs } from 'common';
import Responsive from './react-grid-layout/ResponsiveReactGridLayout';
import Styled from './styles';

/** DEFAULTS */
const RGL_BREAKPOINTS = {
  lg: 1200,
  md: 996,
  sm: 768,
  xs: 480,
  xxs: 0
};

const DEFAULT_RGL_COLS = {
  lg: 4,
  md: 4,
  sm: 2,
  xs: 1,
  xxs: 1
};

const DEFAULT_ROW_HEIGHT = 240;
const DEFAULT_MARGIN = [16, 16];
const DEFAULT_PADDING = [24, 24];
const DEFAULT_ANIMATION_DURATION = 0.4;
const PREVENT_DRAG_DEFAULTS = ['.ccfk-card-expand-button', '.ccfk-card-selection-button'];

interface CardCollectionInterface {
  expandCard?: (any) => void;
  showSelectableCards?: boolean;
  reOrderCards?: () => void
}

const CardCollectionContext = React.createContext<CardCollectionInterface | null>({});

export interface CardCollectionProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onDrag' | 'onDragStart'> {
  /** Card content is placed here. */
  children: React.ReactNode;
  /** Override the default breakpoints. See 'react-grid-layout' npm package. */
  breakpoints?: Record<string, number>;
  /** The number of columns in the card collection grid (x dimension in grid). See 'react-grid-layout' npm package. */
  cols?: Record<string, number>;
  /** The row height in pixels. */
  rowHeight?: number;
  /**
   * The margin between Cards.  Value is an array consisting of 2 numbers,
   * left/right margin, and top/bottom margin.
   * [ horizontal-margin, vertical-margin ]
   */
  margin?: number[];
  /**
   * The padding around the entire Card Collection. Value is an array
   * consisting of 2 numbers, left/right padding, and top/bottom padding.
   * [ horizontal-padding, vertical-padding ]
   */
  padding?: number[];
  /** Callback for when the layout changes. Returns the changed layout. */
  onLayoutChange?: (arg: any[]) => void;
  /** Flag determining whether or not to allow the dragging of child Cards. */
  isDraggable?: boolean;
  /** Prevent certain elements from triggering drag action in the Card. Pass in an array of classnames (don't forget the . prefix). For example: ['.myclass','.myclass2']  */
  preventDrag?: string[];
  /** Callback for when card dragging has started.  All callbacks have signature (layout, oldItem, newItem, placeholder, e, element). See https://github.com/STRML/react-grid-layout/ */
  onDragStart?: (arg: any[]) => void;
  /** Callback for when card is actively dragged.  All callbacks have signature (layout, oldItem, newItem, placeholder, e, element). See https://github.com/STRML/react-grid-layout/ */
  onDrag?: (arg: any[]) => void;
  /** Callback for when card dragging has stopped.  All callbacks have signature (layout, oldItem, newItem, placeholder, e, element). See https://github.com/STRML/react-grid-layout/ */
  onDragStop?: (arg: any[]) => void;
  /** Custom duration for the transform transition on Card. This control the speed of the animation. Measured in seconds. */
  animationDuration?: number;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Forward additional, react-grid-layout props here. See 'react-grid-layout' npm package for more information. */
  reactGridLayoutProps?: {};
  /** Toggles display of checkboxes on all selectable cards, this should be set to true if at least one card is already selected.  */
  showSelectableCards?: boolean;
}

/** A collection of cards layed out in a grid that can be arranged by the user. Please see Readme tab for more information on setup and usage. */
const CardCollection = React.forwardRef<HTMLElement, CardCollectionProps>((props: CardCollectionProps, ref) =>
  getContext(({ RTL: rtl, disableAnimation }) => {
    const {
      rowHeight,
      margin,
      padding,
      isDraggable,
      preventDrag,
      onLayoutChange,
      onDrag,
      onDragStop,
      onDragStart,
      breakpoints,
      cols,
      reactGridLayoutProps,
      showSelectableCards,
      disableAnimation: propsdisableAnimation,
      children,
      ...otherProps
    } = props;
    const containerRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const [layouts, setLayouts] = React.useState({ xxs: [] });
    const [containerDims, setContainerDims] = React.useState({ width: 1280 });
    const currentBreakpoint = getBreakpointFromWidth(breakpoints || RGL_BREAKPOINTS, containerDims.width);
    const resolvedAnimationDisabled = isAnimationDisabled(propsdisableAnimation, disableAnimation);

    // keep track of container width
    const updateContainerDims = () => {
      setContainerDims(combinedRef.current.getBoundingClientRect());
    };

    React.useLayoutEffect(() => {
      updateContainerDims();
      window.addEventListener('resize', updateContainerDims);
      return () => {
        window.removeEventListener('resize', updateContainerDims);
      };
    }, [combinedRef.current]);

    const reOrderCards = (updatedLayout = layouts[currentBreakpoint]) => {
      if (updatedLayout) {
        const sortedLayout = updatedLayout.sort((a, b) => {
          if (a.y > b.y) {
            return 1;
          } else if (a.y < b.y) {
            return -1;
          }
          if (a.x > b.x) {
            return rtl ? -1 : 1; // swap for RTL
          } else if (a.x < b.x) {
            return rtl ? 1 : -1; // swap for RTL
          }
          return 0;
        });
        setLayouts({ ...layouts, [currentBreakpoint]: sortedLayout });
        // loop over and sort out tab-indexes
        sortedLayout.forEach((card, index) => {
          const cardElement = document.getElementById(card.i);
          cardElement && cardElement.setAttribute('data-order', index);
        });
        // loop over and re-arrange in DOM to reflect correct tab-order
        const gridContainer = combinedRef.current.querySelector('.react-grid-layout');
        const item = combinedRef.current.querySelectorAll('.react-grid-layout .react-grid-item');
        const itemArr = [].slice
          .call(item)
          .sort((a, b) => (a.getAttribute('data-order') > b.getAttribute('data-order') ? 1 : -1));
        itemArr.forEach((p) => {
          gridContainer.appendChild(p);
        });
      }
    };

    const onLayoutChangeFn = (...all) => {
      const { 0: updatedLayout } = all;
      setLayouts({ ...layouts, [currentBreakpoint]: updatedLayout });
      onLayoutChange && onLayoutChange(all);
    };

    const onDragStartFn = (...all) => {
      onDragStart && onDragStart(all);
    };

    const onDragFn = (...all) => {
      onDrag && onDrag(all);
    };

    const onWidthChange = () => {
      if (layouts[currentBreakpoint]) {
        reOrderCards(layouts[currentBreakpoint]);
      }
    };

    const onDragStopFn = (...all) => {
      const { 0: updatedLayout } = all;
      if (updatedLayout !== layouts[currentBreakpoint]) {
        reOrderCards(updatedLayout);
        onLayoutChange && onLayoutChange(all);
      }
      onDragStop && onDragStop(updatedLayout);
    };

    const expandCard = (cardId) => {
      const currentLayouts = layouts[currentBreakpoint] || layouts.xxs;
      const updatedLayout = currentLayouts.map((card) => {
        // Only one expanded card at a time.
        if (card.h > 1) {
          return { ...card, h: card.minH ? card.minH : 1, expanded: false };
        }
        if (card.i === cardId) {
          return { ...card, h: card.maxH ? card.maxH : 2, expanded: true };
        }
        return card;
      });
      setLayouts({ ...layouts, [currentBreakpoint]: updatedLayout });
    };

    return (
      <Styled.CardCollection
        $rtl={rtl}
        $disableAnimation={resolvedAnimationDisabled}
        rowHeight={rowHeight}
        ref={combinedRef}
        {...otherProps}
      >
        <CardCollectionContext.Provider
          value={{ expandCard, reOrderCards, showSelectableCards }}
        >
          <Responsive
            width={containerDims.width}
            cols={cols}
            layouts={layouts}
            rowHeight={rowHeight}
            margin={margin}
            containerPadding={padding}
            autoSize
            onLayoutChange={onLayoutChangeFn}
            onDragStart={onDragStartFn}
            onDrag={onDragFn}
            onDragStop={onDragStopFn}
            onWidthChange={onWidthChange}
            isResizable={false}
            isDraggable={isDraggable}
            draggableCancel={
              preventDrag
                ? [...PREVENT_DRAG_DEFAULTS, ...preventDrag].join(',')
                : PREVENT_DRAG_DEFAULTS.join(',')
            }
            useCSSTransforms={false} // Needed to prevent css stacking context with 'stacked' cards
            {...reactGridLayoutProps}
          >
            {children}
          </Responsive>
        </CardCollectionContext.Provider>
      </Styled.CardCollection>
    );
  }));

CardCollection.defaultProps = {
  breakpoints: RGL_BREAKPOINTS,
  cols: DEFAULT_RGL_COLS,
  rowHeight: DEFAULT_ROW_HEIGHT,
  margin: DEFAULT_MARGIN,
  padding: DEFAULT_PADDING,
  isDraggable: true,
  preventDrag: undefined,
  onLayoutChange: undefined,
  onDrag: undefined,
  onDragStop: undefined,
  onDragStart: undefined,
  animationDuration: DEFAULT_ANIMATION_DURATION,
  reactGridLayoutProps: undefined,
  showSelectableCards: false,
  disableAnimation: undefined,
};

CardCollection.displayName = 'CardCollection';
export { CardCollection as default, CardCollectionContext };
